import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertCircle,
  CheckCircle,
  Users,
  TrendingUp,
  BarChart3,
  Mail,
  ArrowRight,
} from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { toast } from "@/hooks/use-toast";
import { useEffect } from "react";

interface TrialExpiredProps {
  reason?: "trialExpired" | "subscriptionInactive";
  trialEndsAt?: string;
  subscriptionStatus?: string;
  companyName?: string;
  onUpgrade?: () => void;
  onDismiss?: () => void;
}

export default function TrialExpired({
  reason = "trialExpired",
  trialEndsAt,
  subscriptionStatus,
  companyName,
  onUpgrade,
  onDismiss: _onDismiss,
}: TrialExpiredProps) {
  const logoutMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/logout");
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to log out");
      }
    },
    onSuccess: () => {
      queryClient.setQueryData(["/api/auth/user"], null);
      window.location.href = "/";
    },
    onError: (error: any) => {
      toast({
        title: "Logout Error",
        description: error.message || "Failed to log out. Please try again.",
        variant: "destructive",
      });
    },
  });

  // ESC key listener for emergency logout
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !logoutMutation.isPending) {
        logoutMutation.mutate();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [logoutMutation]);

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      // Redirect to pricing page where users can select their team size
      // For cancelled subscriptions, skip trial and go straight to paid plans
      const skipTrial = reason === "subscriptionInactive";
      window.location.href = `/pricing?startTrial=${!skipTrial}`;
    }
  };

  // Conditional content based on reason
  const isSubscriptionInactive = reason === "subscriptionInactive";
  const title = isSubscriptionInactive
    ? "Your Subscription is Inactive"
    : "Your 30-Day Trial Has Ended";

  const description = isSubscriptionInactive
    ? `Your TeamPulse® subscription is currently ${subscriptionStatus || "inactive"}${companyName ? ` for ${companyName}` : ""}. Reactivate your subscription to regain access to your team's data and insights.`
    : `${trialEndsAt ? `Your trial period ended on ${new Date(trialEndsAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}. ` : ""}Continue using TeamPulse® to maintain valuable insights into your team's well-being.`;

  const ctaText = isSubscriptionInactive ? "Reactivate Subscription" : "Subscribe Now";

  const features = [
    {
      icon: Users,
      title: "Team Health Insights",
      description: "Track team morale, stress levels, and workload across your entire organization",
    },
    {
      icon: TrendingUp,
      title: "Trend Analysis",
      description: "Visualize patterns and trends over time to make data-driven decisions",
    },
    {
      icon: BarChart3,
      title: "Advanced Analytics",
      description: "Access comprehensive dashboards and executive summaries",
    },
    {
      icon: Mail,
      title: "Automated Reminders",
      description: "Weekly email reminders ensure consistent report submissions",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 flex items-center justify-center">
      <div className="max-w-4xl w-full space-y-6">
        {/* Main Alert Card */}
        <Card className="border-orange-200 bg-white shadow-xl">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto mb-4 w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-orange-600" />
            </div>
            <CardTitle className="text-3xl font-bold">{title}</CardTitle>
            <CardDescription className="text-base mt-2">{description}</CardDescription>
          </CardHeader>
        </Card>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-4">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card key={index} className="bg-white shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Subscription Benefits */}
        <Card className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl text-white">What You Get With TeamPulse®</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <p>Unlimited weekly reports and team members</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <p>Real-time analytics and trend visualization</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <p>Voice transcription for easy report submission</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <p>Role-based access control and team hierarchy</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <p>Automated email reminders and notifications</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <p>Priority support and regular updates</p>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Card className="bg-white shadow-xl">
          <CardContent className="pt-6 space-y-4">
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-6 shadow-md hover:shadow-lg transition-all"
              size="lg"
              onClick={handleUpgrade}
              data-testid="button-subscribe-now"
            >
              {ctaText} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => (window.location.href = "mailto:support@teampulse.us")}
                data-testid="button-contact-support"
              >
                <Mail className="mr-2 h-4 w-4" />
                Contact Support
              </Button>

              <Button
                variant="ghost"
                className="flex-1"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
                data-testid="button-sign-out"
              >
                {logoutMutation.isPending ? "Signing out..." : "Sign Out"}
              </Button>
            </div>

            <p className="text-xs text-gray-500 text-center pt-2">
              Questions about billing or pricing? Contact your administrator or reach out to our
              support team.
            </p>

            <div className="mt-4 p-3 bg-gray-100 rounded-lg text-center">
              <p className="text-xs text-gray-600">
                💡 <strong>Tip:</strong> Press{" "}
                <kbd className="px-2 py-1 bg-white rounded border border-gray-300 text-xs font-mono">
                  ESC
                </kbd>{" "}
                key to sign out if buttons aren't working
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
